import type { DataEmFormatoISO } from '@/tipos';
import type { CachePersistente } from '@/typescript/servicos/cache/cache.interface';
import { CachePersistenteSimplificado } from '@/typescript/servicos/cache/cache';
import { Sincronizador } from '@/typescript/servicos/cache/cache.sync';
import moment from 'moment';

import config from 'src/config';
import {
  Fatura,
  NotificacaoSobreVencimentoDeFatura,
} from '../dominio/faturas.interface';

const sincronizador = new Sincronizador();

interface FaturasCacheadas {
  exp: Date | DataEmFormatoISO;
  faturas: Fatura[];
}

/**
 * @example
 *   > valorAbsolutoDeDiasInteiros(2.25)
 *   3
 *   > valorAbsolutoDeDiasInteiros(-3.56)
 *   4
 */
function valorAbsolutoDeDiasInteiros(dias: number): number {
  return Math.ceil(Math.abs(dias));
}

export async function listarFaturas(
  compManId: string,
  dependencias: { cache: () => Promise<CachePersistente<FaturasCacheadas>> } = {
    async cache() {
      return await CachePersistenteSimplificado.init<FaturasCacheadas>(
        'tiosam.faturas',
      );
    },
  },
): Promise<Fatura[]> {
  const cache = await dependencias.cache();

  const url = `${config.tioSamEndPoint}/faturas/${compManId}`;

  return await sincronizador.sync(async () => {
    const resultado = await cache.comCache(url, async () => {
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: { token: config.tioSamAccessToken },
        });

        const resultado: Fatura[] = await response.json();

        const faturas = resultado.map((f, i) => ({
          id: `${i}-${f.data_faturamento}-${f.valor}`,
          ...f,
        }));

        return {
          exp: moment().add(1, 'hour').utc().toDate(),
          faturas,
        };
      } catch {}
    });

    return resultado?.faturas || [];
  });
}

export async function notificacaoSobreVencimento(
  faturas: Fatura[],
  dependencias: { t: (key: string) => string },
): Promise<NotificacaoSobreVencimentoDeFatura | null> {
  try {
    const faturasEmAberto: Fatura[] = faturas.filter(
      (f) => f.data_vencimento && f.status !== 'Pago' && !f.expirada,
    );

    if (!faturasEmAberto.length) return null;

    const agora = moment().utc();

    const faturaVencida = faturasEmAberto
      .filter((f) => agora.isAfter(moment(f.data_vencimento), 'day'))
      .at(-1);

    if (faturaVencida) {
      const diasDesdeVencimento = valorAbsolutoDeDiasInteiros(
        agora.clone().diff(moment(faturaVencida.data_vencimento), 'days', true),
      );

      return {
        diferencaDeDias: diasDesdeVencimento,
        mensagem: dependencias
          .t('tiosam.faturas.servico.atrasadas')
          .replace('{{vencimento}}', diasDesdeVencimento.toString()),
        tipo: 'danger',
        duracao: 15000,
      };
    }

  } catch {}

  return null;
}
